import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import { X, Plus, Settings, Save, Eye, Loader2 } from 'lucide-react';
import { reportService, Report, ReportFilter } from '@/services/reportService';

interface ReportBuilderProps {
  onClose: () => void;
  onSave: () => void;
  report?: Report;
}

interface ReportFormData {
  name: string;
  description: string;
  type: 'OPERATIONAL' | 'FINANCIAL' | 'CUSTOMER_ANALYTICS' | 'HUB_PERFORMANCE';
  format: 'EXCEL' | 'CSV' | 'JSON' | 'PDF';
  isTemplate: boolean;
  isPublic: boolean;
  cacheEnabled: boolean;
  cacheTTL: number;
  tags: string[];
}

const ReportBuilder: React.FC<ReportBuilderProps> = ({ onClose, onSave, report }) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [filters, setFilters] = useState<ReportFilter>(report?.filters || {});
  const [tagInput, setTagInput] = useState('');
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ReportFormData>({
    defaultValues: {
      name: report?.name || '',
      description: report?.description || '',
      type: report?.type || 'OPERATIONAL',
      format: report?.format || 'EXCEL',
      isTemplate: report?.isTemplate || false,
      isPublic: report?.isPublic || false,
      cacheEnabled: report?.cacheEnabled !== false,
      cacheTTL: report?.cacheTTL || 3600,
      tags: report?.tags || []
    }
  });

  const watchedValues = watch();

  const createReportMutation = useMutation({
    mutationFn: (data: any) => reportService.createReport(data),
    onSuccess: () => {
      toast({
        title: 'Report Created',
        description: 'Your report has been created successfully.',
      });
      onSave();
    },
    onError: (error: any) => {
      toast({
        title: 'Creation Failed',
        description: error.message || 'Failed to create report.',
        variant: 'destructive',
      });
    },
  });

  const updateReportMutation = useMutation({
    mutationFn: (data: any) => reportService.updateReport(report!._id, data),
    onSuccess: () => {
      toast({
        title: 'Report Updated',
        description: 'Your report has been updated successfully.',
      });
      onSave();
    },
    onError: (error: any) => {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update report.',
        variant: 'destructive',
      });
    },
  });

  const isSaving = createReportMutation.isPending || updateReportMutation.isPending;

  const onSubmit = (data: ReportFormData) => {
    const reportData = {
      ...data,
      filters,
      query: generateQuery(data.type, filters)
    };

    if (report) {
      updateReportMutation.mutate(reportData);
    } else {
      createReportMutation.mutate(reportData);
    }
  };

  const generateQuery = (type: string, filters: ReportFilter) => {
    // Generate MongoDB aggregation query based on report type and filters
    const baseMatch: any = {};
    
    if (filters.dateFrom || filters.dateTo) {
      baseMatch.bookingDate = {};
      if (filters.dateFrom) baseMatch.bookingDate.$gte = filters.dateFrom;
      if (filters.dateTo) baseMatch.bookingDate.$lte = filters.dateTo;
    }
    
    if (filters.customer) baseMatch.customer = filters.customer;
    if (filters.company) baseMatch.company = filters.company;
    if (filters.service) baseMatch.service = filters.service;
    if (filters.status) baseMatch.currentStatus = filters.status;
    if (filters.hub) baseMatch.originHub = filters.hub;
    if (filters.vendor) baseMatch.vendor = filters.vendor;

    return { match: baseMatch };
  };

  const addTag = () => {
    if (tagInput.trim() && !watchedValues.tags.includes(tagInput.trim())) {
      const newTags = [...watchedValues.tags, tagInput.trim()];
      setValue('tags', newTags);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    const newTags = watchedValues.tags.filter(tag => tag !== tagToRemove);
    setValue('tags', newTags);
  };

  const getReportTypeDescription = (type: string) => {
    switch (type) {
      case 'OPERATIONAL':
        return 'AWB status summaries, hub performance, and operational metrics';
      case 'FINANCIAL':
        return 'Revenue analysis, costs, profitability, and financial performance';
      case 'CUSTOMER_ANALYTICS':
        return 'Customer behavior, performance trends, and analytics';
      case 'HUB_PERFORMANCE':
        return 'Hub throughput, efficiency metrics, and capacity utilization';
      default:
        return '';
    }
  };

  const steps = [
    { id: 1, title: 'Basic Information', description: 'Report name, type, and description' },
    { id: 2, title: 'Filters & Parameters', description: 'Configure data filters and parameters' },
    { id: 3, title: 'Settings & Options', description: 'Format, caching, and sharing options' }
  ];

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (open) return;
        if (isSaving) {
          toast({
            title: 'Please wait',
            description: `Saving your report…`,
          });
          return;
        }
        onClose();
      }}
    >
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => {
          if (!isSaving) return;
          e.preventDefault();
          toast({
            title: 'Please wait',
            description: `Saving your report…`,
          });
        }}
        onPointerDownOutside={(e) => {
          if (!isSaving) return;
          e.preventDefault();
          toast({
            title: 'Please wait',
            description: `Saving your report…`,
          });
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {report ? 'Edit Report' : 'Create New Report'}
          </DialogTitle>
          <DialogDescription>
            {report ? 'Modify your existing report configuration' : 'Build a custom report with filters and parameters'}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep >= step.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {step.id}
              </div>
              <div className="ml-3 hidden sm:block">
                <div className="text-sm font-medium">{step.title}</div>
                <div className="text-xs text-muted-foreground">{step.description}</div>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-4 ${
                  currentStep > step.id ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Report Name *</Label>
                  <Input
                    id="name"
                    {...register('name', { required: 'Report name is required' })}
                    placeholder="Enter report name"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="type">Report Type *</Label>
                  <Select
                    value={watchedValues.type}
                    onValueChange={(value) => setValue('type', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OPERATIONAL">Operational Reports</SelectItem>
                      <SelectItem value="FINANCIAL">Financial Reports</SelectItem>
                      <SelectItem value="CUSTOMER_ANALYTICS">Customer Analytics</SelectItem>
                      <SelectItem value="HUB_PERFORMANCE">Hub Performance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Describe what this report will show..."
                  rows={3}
                />
              </div>

              {watchedValues.type && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Report Type Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      {getReportTypeDescription(watchedValues.type)}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Filters & Parameters */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Filters</CardTitle>
                  <CardDescription>
                    Configure which data to include in your report
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dateFrom">From Date</Label>
                      <Input
                        id="dateFrom"
                        type="date"
                        value={filters.dateFrom || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="dateTo">To Date</Label>
                      <Input
                        id="dateTo"
                        type="date"
                        value={filters.dateTo || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer">Customer</Label>
                      <Input
                        id="customer"
                        placeholder="Customer ID or name"
                        value={filters.customer || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, customer: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        placeholder="Company ID or name"
                        value={filters.company || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, company: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="minValue">Min Shipment Value</Label>
                      <Input
                        id="minValue"
                        type="number"
                        placeholder="0"
                        value={filters.minValue || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, minValue: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="maxValue">Max Shipment Value</Label>
                      <Input
                        id="maxValue"
                        type="number"
                        placeholder="999999"
                        value={filters.maxValue || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, maxValue: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="paymentMethod">Payment Method</Label>
                      <Select
                        value={filters.paymentMethod || ''}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, paymentMethod: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All methods" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Methods</SelectItem>
                          <SelectItem value="CASH">Cash</SelectItem>
                          <SelectItem value="CHEQUE">Cheque</SelectItem>
                          <SelectItem value="ONLINE">Online</SelectItem>
                          <SelectItem value="CREDIT">Credit</SelectItem>
                          <SelectItem value="PREPAID">Prepaid</SelectItem>
                          <SelectItem value="COD">COD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="service">Service</Label>
                      <Input
                        id="service"
                        placeholder="Service type"
                        value={filters.service || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, service: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={filters.status || ''}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Statuses</SelectItem>
                          <SelectItem value="BOOKED">Booked</SelectItem>
                          <SelectItem value="PICKED_UP">Picked Up</SelectItem>
                          <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
                          <SelectItem value="OUT_FOR_DELIVERY">Out for Delivery</SelectItem>
                          <SelectItem value="DELIVERED">Delivered</SelectItem>
                          <SelectItem value="RETURNED">Returned</SelectItem>
                          <SelectItem value="CANCELLED">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {(watchedValues.type === 'HUB_PERFORMANCE' || watchedValues.type === 'OPERATIONAL') && (
                    <div>
                      <Label htmlFor="hub">Hub</Label>
                      <Input
                        id="hub"
                        placeholder="Hub ID or name"
                        value={filters.hub || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, hub: e.target.value }))}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Settings & Options */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Output Format</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select
                      value={watchedValues.format}
                      onValueChange={(value) => setValue('format', value as any)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EXCEL">Excel (.xlsx)</SelectItem>
                        <SelectItem value="CSV">CSV (.csv)</SelectItem>
                        <SelectItem value="JSON">JSON (.json)</SelectItem>
                        <SelectItem value="PDF">PDF (.pdf)</SelectItem>
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Cache Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="cacheEnabled">Enable Caching</Label>
                      <Switch
                        id="cacheEnabled"
                        checked={watchedValues.cacheEnabled}
                        onCheckedChange={(checked) => setValue('cacheEnabled', checked)}
                      />
                    </div>
                    {watchedValues.cacheEnabled && (
                      <div>
                        <Label htmlFor="cacheTTL">Cache TTL (seconds)</Label>
                        <Input
                          id="cacheTTL"
                          type="number"
                          {...register('cacheTTL', { min: 60, max: 86400 })}
                          placeholder="3600"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Sharing & Visibility</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="isTemplate">Save as Template</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow others to use this report as a template
                      </p>
                    </div>
                    <Switch
                      id="isTemplate"
                      checked={watchedValues.isTemplate}
                      onCheckedChange={(checked) => setValue('isTemplate', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="isPublic">Make Public</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow all users to view and use this report
                      </p>
                    </div>
                    <Switch
                      id="isPublic"
                      checked={watchedValues.isPublic}
                      onCheckedChange={(checked) => setValue('isPublic', checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tags</CardTitle>
                  <CardDescription>
                    Add tags to help organize and find your reports
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a tag..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" onClick={addTag} size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {watchedValues.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {watchedValues.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t">
            <div>
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  disabled={isSaving}
                >
                  Previous
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              
              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={isSaving}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isSaving ? (report ? 'Updating…' : 'Creating…') : (report ? 'Update Report' : 'Create Report')}
                </Button>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportBuilder;